import React from 'react';
import { observer } from 'mobx-react-lite';
import { ElementState } from '../../renderer/ElementState';

import PropertyDropdown from '../common/PropertyDropdown';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import { useStore } from '@src/stores-v2/StoreContext';
import useLayoutAwareFitPropertyState, {
  LAYOUT_TYPES,
  NON_LAYOUT_TYPES,
  isInnerVideoElement,
} from './useLayoutAwareFitPropertyState';

interface Props {
  activeElement: ElementState;
  propertyName: string;
  defaultValue: any;
  options?: Array<{ caption: string; value: any }>;
}

export const VideoFitPropertySelect: React.FC<Props> = observer((props) => {
  const videoCreator = useVideoCreatorStore();
  const { editorStore } = useStore();

  const options = props.options || [
    { caption: 'Cover', value: 'cover' },
    { caption: 'Contain', value: 'contain' },
    { caption: 'Fill', value: 'fill' },
  ];

  // Helper function to update fit property using headless editor or fallback to legacy
  const updateFitPropertyWithEditor = async (newValue: string | null, elementId: string) => {
    console.log('updateFitPropertyWithEditor called', { newValue, elementId, headlessEnabled: editorStore?.headlessEnabled, hasEditor: !!editorStore?.editor });
    
    if (editorStore?.headlessEnabled && editorStore?.editor && elementId) {
      // Use headless editor UpdateElementCommand
      console.log('Attempting headless editor fit update');
      const success = editorStore.editor.commandUpdateElement(elementId, {
        [props.propertyName]: newValue
      });
      
      if (!success) {
        console.warn(`Failed to update fit property for element ${elementId}:`, newValue);
        // Fallback to legacy method
        const modifications = {
          [`${elementId}.${props.propertyName}`]: newValue,
        };
        await videoCreator.applyVideoStateModifications(
          modifications,
          true,
          'applying fit property via fallback',
        );
      } else {
        console.log('Headless editor fit update successful');
      }
    } else {
      console.log('Using legacy method for fit update');
      // Fallback to legacy method
      const modifications = {
        [`${elementId}.${props.propertyName}`]: newValue,
      };
      await videoCreator.applyVideoStateModifications(
        modifications,
        true,
        'applying fit property via legacy',
      );
    }
  };

  const {
    hasMixedOptions,
    updateFitProperty,
    determineValueForStackedLayout,
    handleStackedLayoutFitChange,
  } = useLayoutAwareFitPropertyState({
    options,
    propertyName: props.propertyName,
    defaultValue: props.defaultValue,
    activeElement: props.activeElement,
    updateFitPropertyOverride: updateFitPropertyWithEditor, // Pass our enhanced function
  });

  // First get default value from element properties
  let value =
    props.activeElement.source[props.propertyName] ?? props.defaultValue;
  let elementId = props.activeElement.source.id;

  // Check if we're dealing with the inner video element
  const isInnerVideo = isInnerVideoElement(
    elementId,
    props.activeElement.source.type,
    videoCreator.rootStore.videoSourceStore.source,
  );

  // For inner video elements, just use the direct fit property
  if (isInnerVideo && !hasMixedOptions) {
    console.log('VideoFitPropertySelect: Using inner video path');
    // Make sure the value is valid for a video element
    if (!NON_LAYOUT_TYPES.includes(value)) {
      value = props.defaultValue;
    }
    return (
      <PropertyDropdown
        value={value}
        onChange={async (newValue) => {
          console.log('VideoFitPropertySelect: Inner video onChange called', newValue);
          // Just update the fit property directly
          await updateFitProperty(newValue, elementId);
        }}
        defaultValue={value}
        values={options.map(({ value: optionValue, caption }) => ({
          value: optionValue,
          caption,
          selected: optionValue === value,
        }))}
      />
    );
  }

  console.log('VideoFitPropertySelect: Using stacked layout path', { isInnerVideo, hasMixedOptions });
  value = determineValueForStackedLayout(elementId, value);

  return (
    <PropertyDropdown
      value={value}
      onChange={async (newValue) => {
        console.log('VideoFitPropertySelect: Stacked layout onChange called', newValue);
        await handleStackedLayoutFitChange(newValue);
      }}
      defaultValue={value}
      values={options.map(({ value: optionValue, caption }) => ({
        value: optionValue,
        caption,
        // Force dropdown to show correct selection for all non-layout values
        selected: !LAYOUT_TYPES.includes(optionValue) && optionValue === value,
      }))}
    />
  );
});
